import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { AuthSplitLayout } from './AuthSplitLayout';
import { useResendCooldown } from './useResendCooldown';

const RESEND_COOLDOWN_SECONDS = 61;

export interface ForgotPasswordResult {
  adminManaged: boolean;
  /**
   * Whether the account exists. Only the admin surface ever receives `false`
   * (the BE reveals non-existence for the internal console), which shows a
   * "this account doesn't exist" message. Undefined/true → normal flow.
   */
  accountExists?: boolean;
}

export interface ForgotPasswordPageProps {
  onSendReset: (email: string) => Promise<ForgotPasswordResult | undefined>;
  appName?: string;
}

export function ForgotPasswordPage({
  onSendReset,
  appName = 'Discovr',
}: ForgotPasswordPageProps) {
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [adminManaged, setAdminManaged] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { countdown, start: startCooldown } = useResendCooldown([RESEND_COOLDOWN_SECONDS]);

  const send = async () => {
    setIsSending(true);
    setError(null);
    try {
      const result = await onSendReset(email.trim());
      if (result && result.accountExists === false) {
        // Admin console only — the BE reveals non-existence for the internal tool.
        setNotFound(true);
        setAdminManaged(false);
        setSent(true);
      } else if (result && result.adminManaged) {
        setNotFound(false);
        setAdminManaged(true);
        setSent(true);
      } else {
        setNotFound(false);
        setAdminManaged(false);
        setSent(true);
        startCooldown();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset link. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    void send();
  };

  const handleResend = () => {
    if (countdown > 0 || isSending) return;
    void send();
  };

  void appName; // appName retained for API compatibility (not shown in this layout)

  if (!sent) {
    return (
      <AuthSplitLayout
        backTo="/login"
        headline="Let's get you back in."
        subheadline="Enter the work email tied to your account and we'll send a secure link to reset your password."
        helper="The link expires in 1 hour. Didn't request this? You can safely ignore the email — your password won't change."
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-1">
            <label htmlFor="fp-email" className="text-body-sm-medium text-foreground">Work email</label>
            <Input
              id="fp-email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSending}
              required
              className="h-11 rounded-lg border-border bg-muted focus:bg-card"
            />
          </div>
          <Button
            type="submit"
            disabled={isSending || !email.trim()}
            className="h-11 w-full rounded-full bg-primary text-body-sm-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            Send reset link
          </Button>
          {error && <p className="text-caption-regular text-rose-600">{error}</p>}
        </form>
      </AuthSplitLayout>
    );
  }

  if (notFound) {
    return (
      <AuthSplitLayout
        backTo="/login"
        headline="This account doesn't exist."
        subheadline={`We couldn't find an account for ${email}. Check the address and try again.`}
      >
        <Button
          type="button"
          variant="outline"
          onClick={() => { setSent(false); setNotFound(false); setError(null); }}
          className="h-11 w-full rounded-full"
        >
          Try a different email
        </Button>
      </AuthSplitLayout>
    );
  }

  if (adminManaged) {
    return (
      <AuthSplitLayout
        backTo="/login"
        headline="Reset handled by your admin"
        subheadline="Admin accounts can't be reset here. Ask your super admin to send you a reset link from the admin console."
      >
        <div />
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout
      backTo="/login"
      headline="Check your inbox."
      subheadline={`We sent a reset link to ${email}. Open it on this device to continue.`}
      helper="Can't find it? Check spam, or resend the link. The link works for 1 hour."
    >
      <div className="flex flex-col gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => { setSent(false); setError(null); }}
          className="h-11 w-full rounded-full"
        >
          Use a different email
        </Button>
        <button
          type="button"
          onClick={handleResend}
          disabled={countdown > 0 || isSending}
          className="text-center text-body-sm-medium text-[#6c09f7] hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
        >
          {countdown > 0 ? `Resend in ${countdown}s` : 'Resend link'}
        </button>
      </div>
    </AuthSplitLayout>
  );
}
